<template>
    <div class="min-h-screen bg-white">
        <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
            <div class="mb-8">
                <h1 class="font-header text-[3rem] md:text-[3rem] font-bold text-primary mb-2">
                    Purchase History
                </h1>
                <div v-if="currentUser" class="flex items-center gap-4 mt-4">
                    <div v-if="currentUser.photo_url" class="w-16 h-16 overflow-hidden">
                        <img :src="currentUser.photo_url"
                            :alt="`${currentUser.first_name} ${currentUser.last_name}` || 'User'"
                            class="w-full h-full object-cover" />
                    </div>
                    <div>
                        <p class="font-header text-[1.5rem] font-semibold text-primary">
                            {{ currentUser.first_name && currentUser.last_name ? `${currentUser.first_name}
                            ${currentUser.last_name}` : 'User' }}
                        </p>
                        <p class="font-body text-[1.125rem] text-gray-600 leading-[1.75]">{{ currentUser.email }}</p>
                    </div>
                </div>
                <p v-else class="font-body text-[1.125rem] text-gray-600 leading-[1.75] mt-2">View all your past
                    purchases</p>
            </div>

            <div v-if="loading" class="text-center py-12">
                <p class="text-gray-600 font-sans">Loading...</p>
            </div>

            <div v-else-if="receipts.length === 0 && tickets.length === 0"
                class="bg-gray-50 rounded-lg p-8 text-center">
                <svg class="w-16 h-16 mx-auto text-gray-400 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                        d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
                </svg>
                <p class="font-body text-[1.125rem] text-gray-600 leading-[1.75]">No purchases yet</p>
            </div>

            <div v-else class="space-y-8">
                <section v-if="receipts.length > 0" class="border-t border-gray-200 pt-8">
                    <h2 class="font-header text-[1.5rem] font-bold text-primary mb-6">Purchases</h2>
                    <div class="border border-gray-200 rounded-lg overflow-hidden">
                        <div class="p-6 overflow-x-auto">
                            <table class="w-full">
                                <thead>
                                    <tr>
                                        <th
                                            class="px-4 py-3 text-left font-header font-semibold text-primary border-b-2 border-gray-200">
                                            Item
                                        </th>
                                        <th
                                            class="px-4 py-3 text-left font-header font-semibold text-primary border-b-2 border-gray-200">
                                            Author
                                        </th>
                                        <th
                                            class="px-4 py-3 text-left font-header font-semibold text-primary border-b-2 border-gray-200">
                                            Format
                                        </th>
                                        <th
                                            class="px-4 py-3 text-left font-header font-semibold text-primary border-b-2 border-gray-200">
                                            Quantity</th>
                                        <th
                                            class="px-4 py-3 text-left font-header font-semibold text-primary border-b-2 border-gray-200">
                                            Price
                                        </th>
                                        <th
                                            class="px-4 py-3 text-left font-header font-semibold text-primary border-b-2 border-gray-200">
                                            Order
                                            #</th>
                                        <th
                                            class="px-4 py-3 text-left font-header font-semibold text-primary border-b-2 border-gray-200">
                                            Purchase Date</th>
                                        <th
                                            class="px-4 py-3 text-left font-header font-semibold text-primary border-b-2 border-gray-200">
                                            Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <template v-for="(purchase, index) in allPurchases"
                                        :key="`${purchase.order_number}-${purchase.id || purchase.ticket_code}`">
                                        <tr v-if="index === 0 || allPurchases[index - 1].order_number !== purchase.order_number"
                                            class="border-t-2 border-gray-300">
                                            <td colspan="8" class="px-4 py-3">
                                                <div class="flex items-center justify-between">
                                                    <div>
                                                        <span class="font-header font-bold text-primary">Order #{{
                                                            purchase.order_number }}</span>
                                                        <span
                                                            class="ml-3 font-body text-[1.125rem] text-gray-600 leading-[1.75]">Purchased
                                                            {{
                                                                formatDate(purchase.purchase_date) }}</span>
                                                    </div>
                                                    <div class="flex items-center gap-4">
                                                        <div class="text-right">
                                                            <span
                                                                class="text-xl font-header font-bold text-primary">Total:
                                                                <span data-no-translate class="text-accent">${{
                                                                    purchase.order_total.toFixed(2) }}</span></span>
                                                            <span v-if="purchase.order_discount > 0"
                                                                class="ml-2 text-xs text-green-600 font-body text-[1.125rem] leading-[1.75]">
                                                                Discount<span v-if="purchase.order_discount_code">
                                                                    (<span data-no-translate>{{
                                                                        purchase.order_discount_code
                                                                    }}</span>)</span>: <span data-no-translate>-${{
                                                                            purchase.order_discount.toFixed(2) }}</span>
                                                            </span>
                                                        </div>
                                                        <div class="flex gap-2">
                                                            <button
                                                                @click="viewReceipt(groupedReceipts.find(g => g.transaction_id === purchase.order_number))"
                                                                class="flex items-center gap-1 px-3 py-1 bg-white text-accent border border-accent rounded-md hover:bg-gray-100 transition-colors font-header text-xs font-semibold whitespace-nowrap">
                                                                <svg class="w-3 h-3" fill="none" stroke="currentColor"
                                                                    viewBox="0 0 24 24">
                                                                    <path stroke-linecap="round" stroke-linejoin="round"
                                                                        stroke-width="2"
                                                                        d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                                                    <path stroke-linecap="round" stroke-linejoin="round"
                                                                        stroke-width="2"
                                                                        d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                                                                </svg>
                                                                View
                                                            </button>
                                                            <button
                                                                @click="downloadReceipt(groupedReceipts.find(g => g.transaction_id === purchase.order_number))"
                                                                class="flex items-center gap-1 px-3 py-1 bg-accent text-white rounded-md hover:bg-green-600 transition-colors font-marketing text-xs font-semibold whitespace-nowrap">
                                                                <svg class="w-3 h-3" fill="none" stroke="currentColor"
                                                                    viewBox="0 0 24 24">
                                                                    <path stroke-linecap="round" stroke-linejoin="round"
                                                                        stroke-width="2"
                                                                        d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                                                </svg>
                                                                Download
                                                            </button>
                                                        </div>
                                                    </div>
                                                </div>
                                            </td>
                                        </tr>
                                        <tr class="border-b border-gray-200">
                                            <td class="px-4 py-3">
                                                <template v-if="purchase.book_title">
                                                    <a :href="`/book/${purchase.book_id}`"
                                                        class="font-marketing font-semibold text-primary hover:text-accent transition-colors"
                                                        data-no-translate>
                                                        {{ purchase.book_title }}
                                                    </a>
                                                    <span v-if="purchase.is_deleted"
                                                        class="block mt-1 w-fit px-2.5 py-0.5 rounded-full bg-amber-100 border border-amber-300 text-amber-800 text-xs font-marketing font-semibold">
                                                        The publisher no longer offers this title
                                                    </span>
                                                    <span v-else-if="purchase.is_test"
                                                        class="block mt-1 w-fit px-2.5 py-0.5 rounded-full bg-amber-100 border border-amber-300 text-amber-800 text-xs font-marketing font-semibold">
                                                        The publisher no longer lists this title
                                                    </span>
                                                </template>
                                                <template v-else-if="purchase.event_title">
                                                    <span class="font-marketing font-semibold text-primary"
                                                        data-no-translate>
                                                        {{ purchase.event_title }} (Event Ticket)
                                                    </span>
                                                </template>
                                            </td>
                                            <td class="px-4 py-3 font-sans text-gray-600" data-no-translate>
                                                <template v-if="purchase.book_author">{{ purchase.book_author
                                                }}</template>
                                                <template v-else-if="purchase.ticket_holder_name">{{
                                                    purchase.ticket_holder_name }} (Holder)</template>
                                                <template v-else>-</template>
                                            </td>
                                            <td class="px-4 py-3 font-sans text-gray-600 capitalize">{{ purchase.format
                                                || 'Ticket' }}</td>
                                            <td class="px-4 py-3 font-sans text-gray-600">{{ purchase.quantity }}</td>
                                            <td class="px-4 py-3 font-marketing font-semibold text-primary">
                                                <div data-no-translate>
                                                    <div>${{ parseFloat(purchase.price).toFixed(2) }}</div>
                                                    <div v-if="purchase.original_price && purchase.discount_percentage && parseFloat(purchase.original_price) > parseFloat(purchase.price) && parseFloat(purchase.discount_percentage) > 0"
                                                        class="text-xs text-green-600 mt-1">
                                                        <span class="line-through text-gray-400" data-no-translate>${{
                                                            parseFloat(purchase.original_price).toFixed(2) }}</span>
                                                        <span class="ml-1">{{
                                                            parseFloat(purchase.discount_percentage).toFixed(0) }}%
                                                            off</span>
                                                    </div>
                                                </div>
                                            </td>
                                            <td class="px-4 py-3 font-mono text-sm text-gray-600">{{
                                                purchase.order_number }}</td>
                                            <td class="px-4 py-3 font-sans text-gray-600">{{
                                                formatDate(purchase.purchase_date) }}</td>
                                            <td class="px-4 py-3"></td>
                                        </tr>
                                    </template>
                                </tbody>
                            </table>
                        </div>
                        <div v-if="orderPagination && orderPagination.total_pages > 1"
                            class="p-4 flex justify-between items-center border-t border-gray-200">
                            <button @click="currentOrderPage--" :disabled="!orderPagination.prev_page"
                                :class="['px-4 py-2 rounded-md font-marketing', orderPagination.prev_page ? 'bg-green-700 text-white hover:bg-green-800' : 'bg-gray-300 text-gray-500 cursor-not-allowed']">
                                Previous
                            </button>
                            <span class="font-marketing text-gray-700">
                                Page {{ orderPagination.page }} of {{ orderPagination.total_pages }} ({{
                                    orderPagination.total }}
                                orders)
                            </span>
                            <button @click="currentOrderPage++" :disabled="!orderPagination.next_page"
                                :class="['px-4 py-2 rounded-md font-marketing', orderPagination.next_page ? 'bg-green-700 text-white hover:bg-green-800' : 'bg-gray-300 text-gray-500 cursor-not-allowed']">
                                Next
                            </button>
                        </div>
                    </div>
                </section>

                <section v-if="tickets.length > 0" class="border-t border-gray-200 pt-8">
                    <h2 class="text-3xl font-marketing font-bold text-primary mb-6">Event Tickets</h2>
                    <div class="space-y-6">
                        <div v-if="groupedTickets.valid.length > 0"
                            class="border border-gray-200 rounded-lg overflow-hidden">
                            <button @click="showValidTickets = !showValidTickets"
                                class="w-full p-4 flex items-center justify-between hover:bg-gray-50 transition-colors">
                                <div class="flex items-center gap-3">
                                    <span
                                        class="inline-block px-3 py-1 bg-green-50 text-green-700 rounded-full text-sm font-sans font-semibold">
                                        Valid
                                    </span>
                                    <span class="text-lg font-marketing font-bold text-primary">
                                        Valid Tickets ({{ groupedTickets.valid.length }}<span
                                            v-if="ticketsPagination && ticketsPagination.total_pages > 1"> on this
                                            page</span>)
                                    </span>
                                </div>
                                <svg class="w-5 h-5 text-primary transition-transform"
                                    :class="{ 'rotate-180': showValidTickets }" fill="none" stroke="currentColor"
                                    viewBox="0 0 24 24">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                        d="M19 9l-7 7-7-7" />
                                </svg>
                            </button>
                            <div v-show="showValidTickets" class="border-t border-gray-200">
                                <div class="p-6 overflow-x-auto">
                                    <table class="w-full">
                                        <thead>
                                            <tr>
                                                <th
                                                    class="px-4 py-3 text-left font-marketing font-semibold text-primary border-b-2 border-gray-200">
                                                    Event</th>
                                                <th
                                                    class="px-4 py-3 text-left font-marketing font-semibold text-primary border-b-2 border-gray-200">
                                                    Date</th>
                                                <th
                                                    class="px-4 py-3 text-left font-marketing font-semibold text-primary border-b-2 border-gray-200">
                                                    Location</th>
                                                <th
                                                    class="px-4 py-3 text-left font-marketing font-semibold text-primary border-b-2 border-gray-200">
                                                    Ticket Code
                                                </th>
                                                <th
                                                    class="px-4 py-3 text-left font-marketing font-semibold text-primary border-b-2 border-gray-200">
                                                    Ticket Holder
                                                </th>
                                                <th
                                                    class="px-4 py-3 text-left font-marketing font-semibold text-primary border-b-2 border-gray-200">
                                                    Quantity</th>
                                                <th
                                                    class="px-4 py-3 text-left font-marketing font-semibold text-primary border-b-2 border-gray-200">
                                                    Price</th>
                                                <th
                                                    class="px-4 py-3 text-left font-marketing font-semibold text-primary border-b-2 border-gray-200">
                                                    Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            <tr v-for="ticket in groupedTickets.valid" :key="ticket.id"
                                                class="border-b border-gray-200">
                                                <td class="px-4 py-3 font-marketing text-gray-900" data-no-translate>{{
                                                    ticket.event_title }}</td>
                                                <td class="px-4 py-3 font-sans text-gray-600">{{
                                                    formatDate(ticket.event_date) }}</td>
                                                <td class="px-4 py-3 font-sans text-gray-600">{{ ticket.event_location
                                                }}</td>
                                                <td class="px-4 py-3 font-mono font-bold text-primary">{{
                                                    ticket.ticket_code }}</td>
                                                <td class="px-4 py-3 font-sans text-gray-600">{{
                                                    ticket.ticket_holder_name }}</td>
                                                <td class="px-4 py-3 font-sans text-gray-600">{{ ticket.quantity }}</td>
                                                <td class="px-4 py-3 font-marketing font-semibold text-primary">${{
                                                    parseFloat(ticket.ticket_price !== undefined ? ticket.ticket_price :
                                                        (ticket.total_price !== undefined ? ticket.total_price :
                                                            0)).toFixed(2) }}</td>
                                                <td class="px-4 py-3">
                                                    <div class="flex gap-2">
                                                        <button @click="viewTicket(ticket)"
                                                            class="flex items-center gap-1 px-3 py-1 bg-white text-accent border border-accent rounded-md hover:bg-gray-100 transition-colors font-marketing text-xs font-semibold whitespace-nowrap">
                                                            <svg class="w-3 h-3" fill="none" stroke="currentColor"
                                                                viewBox="0 0 24 24">
                                                                <path stroke-linecap="round" stroke-linejoin="round"
                                                                    stroke-width="2"
                                                                    d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                                                <path stroke-linecap="round" stroke-linejoin="round"
                                                                    stroke-width="2"
                                                                    d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                                                            </svg>
                                                            View
                                                        </button>
                                                        <button @click="downloadTicket(ticket)"
                                                            class="flex items-center gap-1 px-3 py-1 bg-accent text-white rounded-md hover:bg-green-600 transition-colors font-marketing text-xs font-semibold whitespace-nowrap">
                                                            <svg class="w-3 h-3" fill="none" stroke="currentColor"
                                                                viewBox="0 0 24 24">
                                                                <path stroke-linecap="round" stroke-linejoin="round"
                                                                    stroke-width="2"
                                                                    d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                                            </svg>
                                                            Download
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </div>

                        <div v-if="groupedTickets.used.length > 0"
                            class="border border-gray-200 rounded-lg overflow-hidden">
                            <button @click="showUsedTickets = !showUsedTickets"
                                class="w-full p-4 flex items-center justify-between hover:bg-gray-50 transition-colors">
                                <div class="flex items-center gap-3">
                                    <span
                                        class="inline-block px-3 py-1 bg-green-100 text-green-800 rounded-full text-sm font-sans font-semibold">
                                        Used
                                    </span>
                                    <span class="text-lg font-marketing font-bold text-primary">
                                        Used Tickets ({{ groupedTickets.used.length }}<span
                                            v-if="ticketsPagination && ticketsPagination.total_pages > 1"> on this
                                            page</span>)
                                    </span>
                                </div>
                                <svg class="w-5 h-5 text-primary transition-transform"
                                    :class="{ 'rotate-180': showUsedTickets }" fill="none" stroke="currentColor"
                                    viewBox="0 0 24 24">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                        d="M19 9l-7 7-7-7" />
                                </svg>
                            </button>
                            <div v-show="showUsedTickets" class="border-t border-gray-200">
                                <div class="p-6 overflow-x-auto">
                                    <table class="w-full">
                                        <thead>
                                            <tr>
                                                <th
                                                    class="px-4 py-3 text-left font-marketing font-semibold text-primary border-b-2 border-gray-200">
                                                    Event</th>
                                                <th
                                                    class="px-4 py-3 text-left font-marketing font-semibold text-primary border-b-2 border-gray-200">
                                                    Date</th>
                                                <th
                                                    class="px-4 py-3 text-left font-marketing font-semibold text-primary border-b-2 border-gray-200">
                                                    Location</th>
                                                <th
                                                    class="px-4 py-3 text-left font-marketing font-semibold text-primary border-b-2 border-gray-200">
                                                    Ticket Code
                                                </th>
                                                <th
                                                    class="px-4 py-3 text-left font-marketing font-semibold text-primary border-b-2 border-gray-200">
                                                    Ticket Holder
                                                </th>
                                                <th
                                                    class="px-4 py-3 text-left font-marketing font-semibold text-primary border-b-2 border-gray-200">
                                                    Quantity</th>
                                                <th
                                                    class="px-4 py-3 text-left font-marketing font-semibold text-primary border-b-2 border-gray-200">
                                                    Price</th>
                                                <th
                                                    class="px-4 py-3 text-left font-marketing font-semibold text-primary border-b-2 border-gray-200">
                                                    Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            <tr v-for="ticket in groupedTickets.used" :key="ticket.id"
                                                class="border-b border-gray-200">
                                                <td class="px-4 py-3 font-marketing text-gray-900" data-no-translate>{{
                                                    ticket.event_title }}</td>
                                                <td class="px-4 py-3 font-sans text-gray-600">{{
                                                    formatDate(ticket.event_date) }}</td>
                                                <td class="px-4 py-3 font-sans text-gray-600">{{ ticket.event_location
                                                }}</td>
                                                <td class="px-4 py-3 font-mono font-bold text-primary">{{
                                                    ticket.ticket_code }}</td>
                                                <td class="px-4 py-3 font-sans text-gray-600">{{
                                                    ticket.ticket_holder_name }}</td>
                                                <td class="px-4 py-3 font-sans text-gray-600">{{ ticket.quantity }}</td>
                                                <td class="px-4 py-3 font-marketing font-semibold text-primary">${{
                                                    parseFloat(ticket.ticket_price !== undefined ? ticket.ticket_price :
                                                        (ticket.total_price !== undefined ? ticket.total_price :
                                                            0)).toFixed(2) }}</td>
                                                <td class="px-4 py-3">
                                                    <div class="flex gap-2">
                                                        <button @click="viewTicket(ticket)"
                                                            class="flex items-center gap-1 px-3 py-1 bg-white text-accent border border-accent rounded-md hover:bg-gray-100 transition-colors font-marketing text-xs font-semibold whitespace-nowrap">
                                                            <svg class="w-3 h-3" fill="none" stroke="currentColor"
                                                                viewBox="0 0 24 24">
                                                                <path stroke-linecap="round" stroke-linejoin="round"
                                                                    stroke-width="2"
                                                                    d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                                                <path stroke-linecap="round" stroke-linejoin="round"
                                                                    stroke-width="2"
                                                                    d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                                                            </svg>
                                                            View
                                                        </button>
                                                        <button @click="downloadTicket(ticket)"
                                                            class="flex items-center gap-1 px-3 py-1 bg-accent text-white rounded-md hover:bg-green-600 transition-colors font-marketing text-xs font-semibold whitespace-nowrap">
                                                            <svg class="w-3 h-3" fill="none" stroke="currentColor"
                                                                viewBox="0 0 24 24">
                                                                <path stroke-linecap="round" stroke-linejoin="round"
                                                                    stroke-width="2"
                                                                    d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                                            </svg>
                                                            Download
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div v-if="ticketsPagination && ticketsPagination.total_pages > 1"
                        class="mt-4 p-4 bg-white border border-gray-200 rounded-lg flex justify-between items-center">
                        <button @click="loadTickets(currentTicketsPage - 1)" :disabled="!ticketsPagination.prev_page"
                            :class="['px-4 py-2 rounded-md font-marketing', ticketsPagination.prev_page ? 'bg-green-700 text-white hover:bg-green-800' : 'bg-gray-300 text-gray-500 cursor-not-allowed']">
                            Previous
                        </button>
                        <span class="font-marketing text-gray-700">
                            Page {{ ticketsPagination.page }} of {{ ticketsPagination.total_pages }} ({{
                                ticketsPagination.total }}
                            total)
                        </span>
                        <button @click="loadTickets(currentTicketsPage + 1)" :disabled="!ticketsPagination.next_page"
                            :class="['px-4 py-2 rounded-md font-marketing', ticketsPagination.next_page ? 'bg-green-700 text-white hover:bg-green-800' : 'bg-gray-300 text-gray-500 cursor-not-allowed']">
                            Next
                        </button>
                    </div>
                </section>
            </div>
        </div>
    </div>

    <div v-if="showReceiptModal" class="fixed inset-0 z-50 overflow-y-auto" @click.self="closeReceiptModal">
        <div class="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
            <div class="fixed inset-0 transition-opacity bg-black bg-opacity-50" @click="closeReceiptModal"></div>

            <div
                class="relative z-10 inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-4xl sm:w-full">
                <div class="bg-white px-6 py-4 border-b border-gray-200 flex justify-between items-center">
                    <h3 class="text-lg font-marketing font-bold text-primary">Receipt</h3>
                    <button type="button" @click.stop="closeReceiptModal"
                        class="text-gray-400 hover:text-gray-600 transition-colors">
                        <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                </div>
                <div class="bg-white px-6 py-4 max-h-[80vh] overflow-y-auto" v-html="receiptModalContent"></div>
                <div class="bg-gray-50 px-6 py-4 border-t border-gray-200 flex justify-end gap-3">
                    <button type="button" @click.stop="closeReceiptModal"
                        class="px-4 py-2 bg-white text-accent border-2 border-accent rounded-md hover:bg-gray-100 transition-colors font-marketing text-sm font-semibold">
                        Close
                    </button>
                    <button v-if="currentReceiptGroup" @click="downloadReceipt(currentReceiptGroup)"
                        class="px-4 py-2 bg-accent text-white rounded-md hover:bg-green-600 transition-colors font-marketing text-sm font-semibold">
                        Download Receipt
                    </button>
                </div>
            </div>
        </div>
    </div>

    <div v-if="showTicketModal" class="fixed inset-0 z-50 overflow-y-auto" @click.self="closeTicketModal">
        <div class="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
            <div class="fixed inset-0 transition-opacity bg-black bg-opacity-50" @click="closeTicketModal"></div>

            <div
                class="relative z-10 inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-4xl sm:w-full">
                <div class="bg-white px-6 py-4 border-b border-gray-200 flex justify-between items-center">
                    <h3 class="text-lg font-marketing font-bold text-primary">Event Ticket</h3>
                    <button type="button" @click.stop="closeTicketModal"
                        class="text-gray-400 hover:text-gray-600 transition-colors">
                        <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                </div>
                <div class="bg-white px-6 py-4 max-h-[80vh] overflow-y-auto" v-html="ticketModalContent"></div>
                <div class="bg-white px-6 py-4 border-t border-gray-200 flex justify-end gap-3">
                    <button type="button" @click.stop="closeTicketModal"
                        class="px-4 py-2 bg-white text-accent border-2 border-accent rounded-md hover:bg-gray-50 transition-colors font-marketing text-sm font-semibold">
                        Close
                    </button>
                    <button v-if="currentTicket" @click="downloadTicket(currentTicket)"
                        class="px-4 py-2 bg-accent text-white rounded-md hover:bg-green-600 transition-colors font-marketing text-sm font-semibold">
                        Download Ticket
                    </button>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup>
import { generateReceiptHTML } from '../utils/receipt.js'
import { ref, onMounted, computed, watch } from 'vue'
import { userService } from '../services/user'
import { authService } from '../services/auth'
import html2pdf from 'html2pdf.js'
import { formatDate, formatDateFull, formatDateForFilename } from '../utils/date'

const loading = ref(true)
const currentUser = ref(null)
const receipts = ref([])
const tickets = ref([])
const sales = ref([])
const showReceiptModal = ref(false)
const receiptModalContent = ref('')
const currentReceiptGroup = ref(null)
const showTicketModal = ref(false)
const ticketModalContent = ref('')
const currentTicket = ref(null)
const showValidTickets = ref(false)
const showUsedTickets = ref(false)
const currentPage = ref(1)
const limit = ref(50) // Fetch more items to ensure we get complete orders
const pagination = ref(null)
const currentTicketsPage = ref(1)
const ticketsLimit = ref(5)
const ticketsPagination = ref(null)
const totalValidTickets = ref(0)
const totalUsedTickets = ref(0)
const ordersPerPage = ref(3) // Show 3 orders per page
const currentOrderPage = ref(1)

const groupedReceipts = computed(() => {
    const groups = {}
    receipts.value.forEach(receipt => {
        const key = receipt.transaction_id || receipt.id
        if (!groups[key]) {
            const subtotal = parseFloat(receipt.subtotal || 0)
            const discountAmount = parseFloat(receipt.discount_amount || 0)
            const totalAmount = parseFloat(receipt.total_amount || 0)

            groups[key] = {
                transaction_id: key,
                purchase_date: receipt.purchase_date,
                items: [],
                subtotal: subtotal || 0,
                discount_amount: discountAmount || 0,
                discount_code: receipt.discount_code || null,
                total: totalAmount || 0,
                shipping_address: receipt.shipping_address || null,
                billing_address: receipt.billing_address || null,
                customer_name: receipt.customer_name || null,
                delivery_option_id: receipt.delivery_option_id || null,
                delivery_option: receipt.delivery_option || null
            }
        }
        groups[key].items.push(receipt)
        // Trust the total_amount from the database - it has the correct discount-distributed values
        // Do not recalculate using price * quantity as it won't match backend's discount distribution
    })

    // Add ticket-only orders (orders with no book purchases)
    // Use sales table as source of truth for totals
    tickets.value.forEach(ticket => {
        const key = ticket.transaction_id
        if (!groups[key]) {
            // This is a ticket-only order, find the corresponding sale for accurate totals
            const sale = sales.value.find(s => s.order_number === key)
            if (sale) {
                groups[key] = {
                    transaction_id: key,
                    purchase_date: sale.created_at,
                    items: [],
                    subtotal: parseFloat(sale.subtotal || 0),
                    discount_amount: parseFloat(sale.discount_amount || 0),
                    discount_code: sale.discount_code || null,
                    total: parseFloat(sale.total_amount || 0),
                    shipping_address: sale.shipping_address || null,
                    billing_address: sale.billing_address || null,
                    customer_name: sale.customer_name || ticket.ticket_holder_name || null,
                    delivery_option_id: null,
                    delivery_option: null
                }
            } else {
                // Fallback if sale not found (shouldn't happen)
                console.warn(`Sale not found for ticket transaction ${key}`)
                const ticketTotal = parseFloat(ticket.total_price || ticket.ticket_price || 0)
                groups[key] = {
                    transaction_id: key,
                    purchase_date: ticket.purchase_date,
                    items: [],
                    subtotal: ticketTotal,
                    discount_amount: 0,
                    discount_code: null,
                    total: ticketTotal,
                    shipping_address: null,
                    billing_address: null,
                    customer_name: ticket.ticket_holder_name || null,
                    delivery_option_id: null,
                    delivery_option: null
                }
            }
        }
    })

    return Object.values(groups).map(group => {
        // Trust the database totals - they have correct discount-distributed values
        // If subtotal is 0 but we have a total, use total as subtotal (for backwards compatibility)
        if (group.subtotal === 0 && group.total > 0) {
            group.subtotal = group.total
        }
        return group
    }).sort((a, b) => {
        return new Date(b.purchase_date) - new Date(a.purchase_date)
    })
})

// Paginated orders - show only ordersPerPage orders at a time
const paginatedOrders = computed(() => {
    const startIndex = (currentOrderPage.value - 1) * ordersPerPage.value
    const endIndex = startIndex + ordersPerPage.value
    return groupedReceipts.value.slice(startIndex, endIndex)
})

const orderPagination = computed(() => {
    const totalOrders = groupedReceipts.value.length
    const totalPages = Math.ceil(totalOrders / ordersPerPage.value)
    return {
        page: currentOrderPage.value,
        total_pages: totalPages,
        total: totalOrders,
        prev_page: currentOrderPage.value > 1,
        next_page: currentOrderPage.value < totalPages
    }
})

// Unified purchases: books and event tickets for each order
const allPurchases = computed(() => {
    const purchases = []
    paginatedOrders.value.forEach(group => {
        // Add all book items
        group.items.forEach(item => {
            purchases.push({
                ...item,
                order_number: group.transaction_id,
                purchase_date: group.purchase_date,
                order_total: group.total,
                order_discount: group.discount_amount,
                order_discount_code: group.discount_code
            })
        })
        // Add associated event tickets for this order
        const ticketsForOrder = tickets.value.filter(ticket => ticket.transaction_id === group.transaction_id)
        ticketsForOrder.forEach(ticket => {
            purchases.push({
                ...ticket,
                price: ticket.ticket_price !== undefined ? ticket.ticket_price : (ticket.price !== undefined ? ticket.price : (ticket.total_price !== undefined ? ticket.total_price : 0)),
                order_number: group.transaction_id,
                purchase_date: group.purchase_date,
                order_total: group.total,
                order_discount: group.discount_amount,
                order_discount_code: group.discount_code
            })
        })
    })
    return purchases
})

const groupedTickets = computed(() => {
    const valid = tickets.value.filter(ticket => !ticket.is_used)
    const used = tickets.value.filter(ticket => ticket.is_used)

    return {
        valid: valid.sort((a, b) => new Date(b.event_date) - new Date(a.event_date)),
        used: used.sort((a, b) => new Date(b.event_date) - new Date(a.event_date))
    }
})

async function loadTickets(page) {
    loading.value = true
    try {
        const ticketsResult = await userService.getTickets(page, ticketsLimit.value)

        if (ticketsResult.success) {
            tickets.value = ticketsResult.data || []
            ticketsPagination.value = ticketsResult.pagination || null
            currentTicketsPage.value = page

            // Update total counts for valid and used tickets
            if (ticketsResult.data) {
                const validCount = ticketsResult.data.filter(t => !t.is_used).length
                const usedCount = ticketsResult.data.filter(t => t.is_used).length

                // On first page load or when counts change, estimate totals based on pagination
                if (ticketsPagination.value) {
                    totalValidTickets.value = validCount
                    totalUsedTickets.value = usedCount
                }
            }
        }
    } catch (error) {
        console.error('Error loading tickets:', error)
    } finally {
        loading.value = false
    }
}

async function loadData() {
    loading.value = true
    try {
        currentUser.value = authService.getStoredUser()

        if (!currentUser.value) {
            const fetchedUser = await authService.getCurrentUser()
            if (!fetchedUser) {
                window.location.href = '/login'
                return
            }
            currentUser.value = fetchedUser
        }

        const [receiptsResult, ticketsResult, salesResult] = await Promise.all([
            userService.getReceipts(currentPage.value, limit.value),
            userService.getTickets(currentTicketsPage.value, ticketsLimit.value),
            userService.getSales()
        ])

        if (receiptsResult.success) {
            receipts.value = receiptsResult.data || []
            pagination.value = receiptsResult.pagination || null
        }

        if (ticketsResult.success) {
            tickets.value = ticketsResult.data || []
            ticketsPagination.value = ticketsResult.pagination || null
        }

        if (salesResult.success) {
            sales.value = salesResult.data || []
        }
    } catch (error) {
        console.error('Error loading purchase history:', error)
    } finally {
        loading.value = false
    }
}



function viewReceipt(group) {
    currentReceiptGroup.value = group
    // Pass tickets to ensure event tickets are included in the receipt
    const receiptHTML = generateReceiptHTML(group, true, currentUser.value, tickets.value)
    receiptModalContent.value = receiptHTML
    showReceiptModal.value = true
}

function closeReceiptModal() {
    showReceiptModal.value = false
    receiptModalContent.value = ''
    currentReceiptGroup.value = null
}

function viewTicket(ticket) {
    currentTicket.value = ticket
    const ticketHTML = generateTicketHTMLForModal(ticket)
    ticketModalContent.value = ticketHTML
    showTicketModal.value = true
}

function closeTicketModal() {
    showTicketModal.value = false
    ticketModalContent.value = ''
    currentTicket.value = null
}

// Watch for modals close to ensure body scroll is always restored
watch(showReceiptModal, (isOpen) => {
    document.body.style.overflow = isOpen ? 'hidden' : ''
})

watch(showTicketModal, (isOpen) => {
    document.body.style.overflow = isOpen ? 'hidden' : ''
})

async function downloadReceipt(group) {
    // Pass tickets to ensure event tickets are included in the receipt
    const receiptHTML = generateReceiptHTML(group, false, currentUser.value, tickets.value)

    const tempWindow = window.open('', '_blank')
    if (!tempWindow) {
        alert('Please allow popups to download the PDF')
        return
    }

    tempWindow.document.write(receiptHTML)
    tempWindow.document.close()

    await new Promise(resolve => setTimeout(resolve, 500))

    const element = tempWindow.document.querySelector('.receipt-container') || tempWindow.document.body

    const opt = {
        margin: 0.5,
        filename: `receipt-${group.transaction_id}-${formatDateForFilename(group.purchase_date)}.pdf`,
        image: { type: 'jpeg', quality: 0.98 },
        html2canvas: { scale: 2, useCORS: true, windowWidth: element.scrollWidth, windowHeight: element.scrollHeight },
        jsPDF: { unit: 'in', format: 'letter', orientation: 'portrait' }
    }

    try {
        await html2pdf().set(opt).from(element).save()
        tempWindow.close()
    } catch (error) {
        console.error('Error generating PDF:', error)
        tempWindow.close()
        alert('Failed to generate PDF. Please try again.')
    }
}

async function downloadTicket(ticket) {
    const ticketHTML = generateTicketHTML(ticket)

    const tempWindow = window.open('', '_blank')
    if (!tempWindow) {
        alert('Please allow popups to download the PDF')
        return
    }

    tempWindow.document.write(ticketHTML)
    tempWindow.document.close()

    await new Promise(resolve => setTimeout(resolve, 500))

    const element = tempWindow.document.querySelector('.ticket-container') || tempWindow.document.body

    const opt = {
        margin: 0.5,
        filename: `ticket-${ticket.ticket_code}-${formatDateForFilename(ticket.purchase_date)}.pdf`,
        image: { type: 'jpeg', quality: 0.98 },
        html2canvas: { scale: 2, useCORS: true, windowWidth: element.scrollWidth, windowHeight: element.scrollHeight },
        jsPDF: { unit: 'in', format: 'letter', orientation: 'portrait' }
    }

    try {
        await html2pdf().set(opt).from(element).save()
        tempWindow.close()
    } catch (error) {
        console.error('Error generating PDF:', error)
        tempWindow.close()
        alert('Failed to generate PDF. Please try again.')
    }
}

function generateTicketHTMLForModal(ticket) {
    const logoUrl = (typeof window !== 'undefined' ? window.location.origin : '') + '/logos/Horizontal%20Logo/No%20Background%20Black%20Logo%20Horizontal.png'
    const companyLogo = `<img src="${logoUrl}" alt="Barry & Bonna Publishing" style="max-width: 100%; height: auto; max-height: 120px; display: block; margin: 0 auto 20px auto;" />`
    const ticketDate = formatDateFull(ticket.event_date)
    const purchaseDate = formatDateFull(ticket.purchase_date)
    const ticketCode = ticket.ticket_code
    const eventTitle = ticket.event_title
    const location = ticket.event_location || 'TBA'
    const ticketHolder = ticket.ticket_holder_name
    const quantity = ticket.quantity
    const totalPrice = parseFloat(ticket.total_price || 0)
    const status = ticket.is_used ? 'Used' : 'Valid'

    return `
        <div style="font-family: 'Georgia', serif; color: #253439;">
            <div style="text-align: center; margin-bottom: 30px; padding-bottom: 20px; border-bottom: 2px solid #2D4D31;">
                ${companyLogo}
                <h1 style="color: #2D4D31; font-size: 24px; margin: 10px 0;">Event Ticket</h1>
            </div>
            
            <div style="margin-bottom: 30px;">
                <p style="margin: 5px 0; font-size: 14px; color: #253439;"><strong>Transaction ID:</strong> ${escapeHtml(ticket.transaction_id)}</p>
                <p style="margin: 5px 0; font-size: 14px; color: #253439;"><strong>Purchase Date:</strong> ${purchaseDate}</p>
                <p style="margin: 5px 0; font-size: 14px; color: #253439;"><strong>Ticket Holder:</strong> ${escapeHtml(ticketHolder)}</p>
                <p style="margin: 5px 0; font-size: 14px; color: #253439;"><strong>Quantity:</strong> ${quantity}</p>
                <p style="margin: 5px 0; font-size: 14px; color: #253439;"><strong>Total Price:</strong> $${totalPrice.toFixed(2)}</p>
            </div>
            
            <div style="background-color: #FAFAFA; padding: 20px; border-radius: 8px; margin: 20px 0;">
                <h2 style="font-family: 'EB Garamond', serif; color: #253439; margin: 0 0 15px 0; font-size: 24px; font-weight: normal;" data-no-translate>${escapeHtml(eventTitle)}</h2>
                <p style="margin: 8px 0; font-size: 16px; color: #253439;"><strong>Date:</strong> ${ticketDate}</p>
                <p style="margin: 8px 0; font-size: 16px; color: #253439;"><strong>Location:</strong> ${escapeHtml(location)}</p>
            </div>
            
            <div style="background-color: #FAFAFA; border: 2px dashed #2D4D31; border-radius: 8px; padding: 30px; text-align: center; margin: 30px 0;">
                <div style="color: #7C898B; font-size: 12px; text-transform: uppercase; letter-spacing: 1px; margin-bottom: 10px;">Ticket Code</div>
                <div style="color: #2D4D31; font-size: 36px; font-weight: bold; letter-spacing: 4px; font-family: monospace;">${escapeHtml(ticketCode)}</div>
                <div style="display: inline-block; padding: 8px 16px; border-radius: 20px; font-size: 14px; font-weight: 600; margin-top: 10px; ${ticket.is_used ? 'background-color: #D1FAE5; color: #065F46;' : 'background-color: #FEF3C7; color: #92400E;'}">${status}</div>
            </div>
            
            <div style="margin-top: 30px; padding-top: 20px; border-top: 1px solid #E8E8E8; text-align: center; color: #7C898B; font-size: 12px;">
                <p style="margin: 5px 0;">Thank you for your purchase!</p>
                <p style="margin: 5px 0;">© ${new Date().getFullYear()} <i>Barry & Bonna Publishing</i> LLC. All rights reserved.</p>
            </div>
        </div>
    `
}

function generateTicketHTML(ticket) {
    const logoUrl = (typeof window !== 'undefined' ? window.location.origin : '') + '/logos/Horizontal%20Logo/No%20Background%20Black%20Logo%20Horizontal.png'
    const companyLogo = `<img src="${logoUrl}" alt="Barry & Bonna Publishing" style="max-width: 100%; height: auto; max-height: 120px; display: block; margin: 0 auto 20px auto;" />`
    const ticketDate = formatDateFull(ticket.event_date)
    const purchaseDate = formatDateFull(ticket.purchase_date)
    const ticketCode = ticket.ticket_code
    const eventTitle = ticket.event_title
    const location = ticket.event_location || 'TBA'
    const ticketHolder = ticket.ticket_holder_name
    const quantity = ticket.quantity
    const totalPrice = parseFloat(ticket.total_price || 0)

    return `
<!DOCTYPE html>
<html lang="en">
<head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>Event Ticket - ${escapeHtml(eventTitle)}</title>
    <style>
        @media print {
            body { margin: 0; }
            .no-print { display: none; }
        }
        body {
            font-family: 'Georgia', serif;
            color: #253439;
            background: #FFF5EA;
            padding: 40px 20px;
            margin: 0;
        }
        .ticket-container {
            max-width: 800px;
            margin: 0 auto;
            background: #FFFFFF;
            border-radius: 8px;
            padding: 40px;
            box-shadow: 0 2px 8px rgba(0,0,0,0.1);
        }
    </style>
</head>
<body>
    <div class="ticket-container">
        <div style="text-align: center; margin-bottom: 30px; padding-bottom: 20px; border-bottom: 2px solid #2D4D31;">
            ${companyLogo}
            <h1 style="color: #2D4D31; font-size: 24px; margin: 10px 0;">Event Ticket</h1>
        </div>
        
        <div style="margin-bottom: 30px;">
            <p style="margin: 5px 0; font-size: 14px; color: #253439;"><strong>Transaction ID:</strong> ${escapeHtml(ticket.transaction_id)}</p>
            <p style="margin: 5px 0; font-size: 14px; color: #253439;"><strong>Purchase Date:</strong> ${purchaseDate}</p>
            <p style="margin: 5px 0; font-size: 14px; color: #253439;"><strong>Ticket Holder:</strong> ${escapeHtml(ticketHolder)}</p>
            <p style="margin: 5px 0; font-size: 14px; color: #253439;"><strong>Quantity:</strong> ${quantity}</p>
            <p style="margin: 5px 0; font-size: 14px; color: #253439;"><strong>Total Price:</strong> $${totalPrice.toFixed(2)}</p>
        </div>
        
        <div style="background-color: #FAFAFA; padding: 20px; border-radius: 8px; margin: 20px 0;">
            <h2 style="font-family: 'EB Garamond', serif; color: #253439; margin: 0 0 15px 0; font-size: 24px; font-weight: normal;" data-no-translate>${escapeHtml(eventTitle)}</h2>
            <p style="margin: 8px 0; font-size: 16px; color: #253439;"><strong>Date:</strong> ${ticketDate}</p>
            <p style="margin: 8px 0; font-size: 16px; color: #253439;"><strong>Location:</strong> ${escapeHtml(location)}</p>
        </div>
        
        <div style="background-color: #FAFAFA; border: 2px dashed #2D4D31; border-radius: 8px; padding: 30px; text-align: center; margin: 30px 0;">
            <div style="color: #7C898B; font-size: 12px; text-transform: uppercase; letter-spacing: 1px; margin-bottom: 10px;">Ticket Code</div>
            <div style="color: #2D4D31; font-size: 36px; font-weight: bold; letter-spacing: 4px; font-family: monospace;">${escapeHtml(ticketCode)}</div>
        </div>
        
        <div style="margin-top: 30px; padding-top: 20px; border-top: 1px solid #E8E8E8; text-align: center; color: #7C898B; font-size: 12px;">
            <p style="margin: 5px 0;">Thank you for your purchase!</p>
            <p style="margin: 5px 0;">© ${new Date().getFullYear()} <i>Barry & Bonna Publishing</i> LLC. All rights reserved.</p>
        </div>
    </div>
</body>
</html>
    `
}


// formatDeliveryOption is now imported from utils/receipt.js

// formatAddressSection is now imported from utils/receipt.js

// generateReceiptHTML is now imported from utils/receipt.js

function escapeHtml(text) {
    const div = document.createElement('div')
    div.textContent = text
    return div.innerHTML
}

onMounted(() => {
    loadData()
})
</script>
